import React, { useEffect } from "react";
import { useIsMobile } from "@/lib/hooks/use-mobile";
import { useMobileAppLayoutSafe } from "@/lib/providers/mobile-app-layout-context";
import { cn } from "@/lib/utils";

interface PageContentProps {
  className?: string;
  children: React.ReactNode;
  /**
   * Override the default responsive behavior
   * - 'auto': Automatically choose based on device (default)
   * - 'mobile': Force mobile layout (fixed header, scrollable content)
   * - 'desktop': Force desktop layout (full page scroll)
   */
  layout?: "auto" | "mobile" | "desktop";
  /**
   * Custom padding override
   * If not provided, uses responsive defaults
   */
  padding?: string;
}

/**
 * PageContent component that handles responsive scrolling behavior
 *
 * Mobile: iOS-style fixed header with content scrolling underneath
 * Desktop: Full-page scrolling where navigation and content scroll together
 *
 * IMPORTANT: Only use at the page level, never in nested components
 */
export const PageContent: React.FC<PageContentProps> = ({
  children,
  className = "mt-2 md:mt-4",
  layout = "auto",
  padding,
}) => {
  const isMobile = useIsMobile();

  // Use safe context hook that doesn't throw if provider isn't available
  const mobileLayoutContext = useMobileAppLayoutSafe();
  const navBarHeight = mobileLayoutContext?.navBarHeight || 0;
  const tabBarHeight = mobileLayoutContext?.tabBarHeight || 0;
  const setScrolled = mobileLayoutContext?.setScrolled;

  // Reset scroll state on mount (when page changes)
  useEffect(() => {
    if (setScrolled) {
      setScrolled(false);
    }
  }, [setScrolled]);

  // Set up scroll detection for both mobile and desktop using context
  useEffect(() => {
    if (!setScrolled) return; // Run on both platforms when context is available

    let rafId: number;
    const handleScroll = (event: Event) => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        const target = event.target as HTMLElement;
        if (target) {
          const scrolled = target.scrollTop > 10;
          setScrolled(scrolled);
        }
      });
    };

    const scrollContainer = document.querySelector("[data-page-content]");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll, {
        passive: true,
      });

      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        scrollContainer.removeEventListener("scroll", handleScroll);
      };
    }
  }, [setScrolled]);

  // Determine effective layout
  const effectiveLayout = layout === "auto" ? (isMobile ? "mobile" : "desktop") : layout;

  // Base styles that apply to both layouts
  const baseStyles = "flex-1";

  // Layout-specific styles
  const layoutStyles = {
    mobile: [
      // Mobile: Fixed header approach - content scrolls underneath
      "overflow-y-auto", // Make content scrollable
      "overflow-x-hidden", // Prevent horizontal scroll on mobile
      "overscroll-behavior-y-contain", // Prevent overscroll bounce on iOS
      "-webkit-overflow-scrolling-touch", // Smooth scrolling on iOS
      "pb-safe-bottom", // Add bottom safe area padding
    ].join(" "),

    desktop: [
      // Desktop: Now handles its own scrolling since AppLayout unified
      "overflow-y-auto", // Make content scrollable on desktop too
      "overflow-x-hidden", // Prevent horizontal scroll on desktop
      "overscroll-behavior-y-contain", // Consistent scroll behavior
    ].join(" "),
  };

  // Responsive padding
  const paddingStyles = padding || "px-2";

  // The nav bars are fixed, and so is the global paused-bookings banner they sit
  // below — add its published height so scrolled content clears both.
  const withBannerOffset = (navOffset: string) =>
    `calc(${navOffset} + var(--paused-banner-height, 0px))`;

  // Padding styles for fixed nav bar and tab bar (both mobile and desktop)
  const layoutPaddingStyles =
    effectiveLayout === "mobile"
      ? {
          paddingTop: withBannerOffset(navBarHeight > 0 ? `${navBarHeight}px` : "0px"),
          paddingBottom: tabBarHeight > 0 ? `${tabBarHeight}px` : undefined,
        }
      : effectiveLayout === "desktop"
        ? {
            // Use measured height or fallback
            paddingTop: withBannerOffset(navBarHeight > 0 ? `${navBarHeight}px` : "5rem"),
          }
        : {};

  return (
    <main
      data-page-content
      style={layoutPaddingStyles}
      className={cn(baseStyles, layoutStyles[effectiveLayout], paddingStyles, className)}
      aria-label="Main content"
    >
      {/* Mobile: Add top padding to account for fixed header */}
      {effectiveLayout === "mobile" && <div className="flex-shrink-0" aria-hidden="true" />}

      <div id="sticky-portal" className="sticky z-20" style={{ top: 0 }} />

      <div
        className={cn(
          "min-h-full",
          // Add proper spacing for mobile content
          effectiveLayout === "mobile" && "py-0"
        )}
      >
        {children}
      </div>

      {/* Mobile: Add bottom padding to account for mobile tab bar */}
      {effectiveLayout === "mobile" && (
        <div className="h-safe-bottom flex-shrink-0" aria-hidden="true" />
      )}
    </main>
  );
};
